import React from 'react'

import { Stack, Divider, Box, Typography, Avatar, useTheme } from '@mui/material'
import { UserRound } from 'lucide-react'

interface OwnerBlockProps {
  owner: string | undefined | null
  title?: string
  imageUrl?: string
  subTitle?: string | undefined | null
  additionalTitle?: string | undefined
  isLast?: boolean
  onClick?: () => void
}

export const OwnerBlock = ({
  owner,
  title,
  imageUrl,
  subTitle,
  additionalTitle,
  isLast = false,
  onClick,
}: OwnerBlockProps) => {
  const { palette } = useTheme()

  // "Anonymous" is a placeholder, not a person — reducing it to "AN" would invent an
  // identity we don't have, so it falls through to the silhouette.
  const isAnonymous = !owner || String(owner).trim().toLowerCase() === 'anonymous'
  const initials = isAnonymous
    ? ''
    : String(owner)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0])
        .join('')
        .toUpperCase()

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={onClick}>
        <Stack direction="row" spacing="16px" alignItems="center">
          {/* Three-step fallback, matching the map's stack popover exactly: photo →
              INITIALS → the UserRound outline. Initials matter because the silhouette is
              identical for every person, so two different known scanners rendered the
              same glyph and the row lost the one thing it was showing. The outline is
              reserved for a genuinely anonymous scan, where there is no name to reduce.
              MUI's own filled Person silhouette is deliberately not used — the lucide
              outline matches the list rows and the platform treatment. VERA-476. */}
          <Avatar
            alt={owner as string}
            src={imageUrl || undefined}
            sx={theme => ({
              backgroundColor: theme.palette.grey[100],
              color: theme.palette.text.secondary,
              border: `1px solid ${theme.palette.grey[300]}`,
              fontSize: 14,
              fontWeight: 600,
            })}
          >
            {initials || <UserRound size={20} strokeWidth={1.5} />}
          </Avatar>

          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle1" mb="2px" sx={{ lineHeight: '17px' }}>
              {owner}
            </Typography>

            {subTitle && (
              <Typography variant="body2" sx={theme => ({ color: theme.palette.grey[400] })}>
                {subTitle}
              </Typography>
            )}

            <Typography variant="body2">{title}</Typography>

            {additionalTitle && <Typography variant="body2">{additionalTitle}</Typography>}
          </Box>
        </Stack>
      </Box>
      {isLast && <Divider sx={{ margin: '12px 0' }} />}
    </>
  )
}
